/**
 * Pezzi condivisi del filtro spam: l'etichetta di un verdetto e l'elenco dei
 * motivi. I motivi li scrive l'AI a partire dal testo dell'email, quindi si
 * mostrano sempre e solo come testo semplice.
 */
import { AlertTriangle, CheckCircle2, HelpCircle, ShieldAlert, Undo2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { VoceSpam } from '@/lib/api/endpoints';

export interface Presentazione {
  etichetta: string;
  /** Classi del riquadro e del testo. */
  tono: 'verde' | 'ambra' | 'rosso' | 'azzurro' | 'neutro';
  icona: typeof CheckCircle2;
  titolo: string;
}

export function presenta(v: Pick<VoceSpam, 'verdict' | 'action' | 'decided_by'>): Presentazione {
  if (v.action === 'rescued') {
    return { etichetta: 'Rimessa in arrivo', tono: 'azzurro', icona: Undo2, titolo: 'Rimessa in arrivo' };
  }
  switch (v.verdict) {
    case 'legit':
      return { etichetta: 'Forse non è spam', tono: 'verde', icona: CheckCircle2, titolo: 'Potrebbe non essere spam' };
    case 'uncertain':
      return { etichetta: 'Da controllare', tono: 'ambra', icona: HelpCircle, titolo: 'Il filtro non è sicuro: controllala tu' };
    case 'phishing':
      return { etichetta: 'Pericolosa', tono: 'rosso', icona: ShieldAlert, titolo: 'Sembra una truffa (phishing)' };
    case 'spam':
      return v.action === 'filtered'
        ? { etichetta: 'Spostata dal filtro', tono: 'neutro', icona: AlertTriangle, titolo: 'Spostata nello spam dal filtro' }
        : { etichetta: 'Spam', tono: 'neutro', icona: AlertTriangle, titolo: 'Spam' };
    default:
      return { etichetta: 'Spam', tono: 'neutro', icona: AlertTriangle, titolo: 'Spam' };
  }
}

const TONI: Record<Presentazione['tono'], string> = {
  verde: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  ambra: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  rosso: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
  azzurro: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300',
  neutro: 'bg-muted text-muted-foreground',
};

export const RIQUADRI: Record<Presentazione['tono'], string> = {
  verde: 'border-emerald-300 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40',
  ambra: 'border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40',
  rosso: 'border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/40',
  azzurro: 'border-sky-300 bg-sky-50 dark:border-sky-900 dark:bg-sky-950/40',
  neutro: 'border-border bg-muted/40',
};

export function ChipVerdetto({ voce, className }: { voce: Pick<VoceSpam, 'verdict' | 'action' | 'decided_by'>; className?: string }) {
  const p = presenta(voce);
  // Lo spam semplice non ha bisogno di un'etichetta: è la cartella dello spam.
  if (p.tono === 'neutro' && voce.action !== 'filtered') return null;
  const Icona = p.icona;
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium', TONI[p.tono], className)}>
      <Icona className="h-3 w-3" />
      {p.etichetta}
    </span>
  );
}

export function Motivi({ motivi, className }: { motivi: string[]; className?: string }) {
  if (motivi.length === 0) return null;
  return (
    <ul className={cn('space-y-1 text-sm', className)}>
      {motivi.map((m, i) => (
        <li key={i} className="flex gap-2">
          <span aria-hidden className="text-muted-foreground">•</span>
          <span>{m}</span>
        </li>
      ))}
    </ul>
  );
}
